import { FC, Fragment, useEffect, useState } from 'react';
import {
	Button,
	Checkbox,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Space,
	Table,
	Typography,
} from 'antd';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import DataRow from '@/libs/antd/components/DataRow';
import {
	SubmitCCASurveyAnalysisPayload,
	getSurveyAnalysisDetail,
	submitCCAReturnAnalysis,
	submitCCASurveyAnalysis,
} from '@/libs/fleet-api/services/survey';
import FleetTitle from '@/libs/antd/components/Title';
import { useForm } from '@/libs/antd/form';
import dayjs from 'dayjs';
import TextArea from 'antd/es/input/TextArea';
import rule from '@/libs/antd/rules';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { formatCurrency } from '@/libs/antd/formater';
import { useApp } from '@/App';
import { useModals } from '@/components/modals/ModalProvider';
import { DocumentItem } from '@/components/modals/DocumentModal';
import fetcher, { FleetFile } from '@/libs/fleet-api/fetcher';
import { ProspectFinancialReport } from '@/libs/fleet-api/services/types';
import GenericErrorMessage from '@/components/GenericErrorMessage';

type CCASurveyVerificationPageProps = {
	prospectId: string;
};

const CCASurveyVerificationPage: FC<CCASurveyVerificationPageProps> = ({
	prospectId = '',
}) => {
	const { navigate, notification } = useApp();
	const { form, value, fieldName } = useForm<SubmitCCASurveyAnalysisPayload>();
	const {
		modal,
		openPreviewModal,
		openCustomerDetail,
		openCustomerGroupDetail,
		openCustomerCheckingModal,
		openDocumentModal,
		openProspectDetail,
		openFinancialReportModal,
		openEmploymentContractModal,
		openBankStatementModal,
		openMapModal,
	} = useModals();
	const { data, isLoading, isError } = useQuery({
		queryKey: ['getSurveyAnalysisDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getSurveyAnalysisDetail(id);
		},
	});

	const submitCCASurveyAnalysisMutation = useMutation({
		mutationFn: submitCCASurveyAnalysis,
	});

	const submitCCAReturnAnalysisMutation = useMutation({
		mutationFn: submitCCAReturnAnalysis,
	});

	const {
		mainInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
		employmentContracts,
		prospectFinancialReports,
		prospectFinancialReportAnalysis,
		prospectInformation,
		prospectBankStatements,
		approvalHistories,
		surveyInformation,
	} = data?.data ?? {};

	const surveyParticipants = surveyInformation?.surveyParticipants;
	const [checkedFinancialId, setCheckedFinancialId] = useState<number[]>([]);
	const [officeAddress, setOfficeAddress] = useState<string>('');
	const [siteAddress, setSiteAddress] = useState<string>('');
	const [officeLat, setOfficeLat] = useState<number>(0);
	const [officeLong, setOfficeLong] = useState<number>(0);
	const [siteLat, setSiteLat] = useState<number>(0);
	const [siteLong, setSiteLong] = useState<number>(0);

	useEffect(() => {
		if (surveyInformation) {
			setOfficeAddress(surveyInformation.officeAddress);
			setSiteAddress(surveyInformation.siteAddress);
			setOfficeLat(surveyInformation.officeLat);
			setOfficeLong(surveyInformation.officeLong);
			setSiteLat(surveyInformation.siteLat);
			setSiteLong(surveyInformation.siteLong);
		}
	}, [surveyInformation]);
	useEffect(() => {
		if (
			prospectFinancialReportAnalysis &&
			prospectFinancialReportAnalysis.length
		) {
			setCheckedFinancialId(
				prospectFinancialReportAnalysis.map(
					(report) => report.financialReportId,
				),
			);
		}
	}, [prospectFinancialReportAnalysis]);

	const handleSubmit = async (values: SubmitCCASurveyAnalysisPayload) => {
		if (values.financialReportAnalysis) {
			try {
				let financialReportAnalysis = values.financialReportAnalysis;

				if (!prospectFinancialReportAnalysis?.length) {
					financialReportAnalysis = values.financialReportAnalysis.map(
						(item) => {
							return {
								...item,
								id: null,
							};
						},
					);
				}
				await submitCCASurveyAnalysisMutation.mutateAsync({
					...values,
					financialReportAnalysis,
				});
				notification.success({
					message: 'Success',
					description: 'Data has been saved',
				});
				navigate('CCASurveyVerificationListPage');
			} catch (error) {
				if (fetcher.isError(error)) {
					const message = await error.json();
					notification.error({
						duration: 20,
						message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
						description: (
							<div>
								{message?.metadata?.debugInfo?.messages &&
								message?.metadata?.debugInfo?.messages.length ? (
									<ul>
										{message?.metadata?.debugInfo?.messages.map(
											(msg: string) => <li>{msg}</li>,
										)}
									</ul>
								) : (
									<pre>Opps, Something went wrong</pre>
								)}
							</div>
						),
					});
				}
			}
		} else {
			notification.error({
				message: 'Error',
				description: 'Anda harus mengisi komparasi laporan keuangan',
			});
			const element = document.getElementById('laporan-keuangan');
			if (element) {
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
					inline: 'nearest',
				});
				window.scrollBy(0, -300);
			}
		}
	};

	if (isError) return <GenericErrorMessage />;

	if (isLoading)
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);

	return (
		<Fragment>
			<FleetTitle>Main Information</FleetTitle>
			<DataRow
				loading={isLoading}
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerDetail(mainInformation!.customerId);
								}}
							>
								{mainInformation?.customerName}
							</a>,
						],
						[
							'Prospect ID',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openProspectDetail(mainInformation!.prospectId);
								}}
							>
								{mainInformation?.prospectId}
							</a>,
						],
					],
					[
						['Kelas Industri', mainInformation?.industrySector.industryClass],
						['Sumber Order', mainInformation?.orderSource],
					],
					[
						[
							'Portofolio Guideline',
							mainInformation?.industrySector.industryClass,
						],
						[
							'Group Customer BMPP',
							mainInformation?.customerGroup?.id ? (
								<a
									href="#"
									className="trigger-popup"
									onClick={(e) => {
										e.preventDefault();
										openCustomerGroupDetail(mainInformation!.customerGroup.id);
									}}
									aria-disabled={!mainInformation?.customerGroup.value}
								>
									{mainInformation?.customerGroup.value ?? '-'}
								</a>
							) : (
								'-'
							),
						],
					],
					[
						[
							'Analisa Customer Checking',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();
									openCustomerCheckingModal(prospectId);
								}}
							>
								Detail Analisa
							</a>,
						],
						[
							'Dokumen',
							<a
								href="#"
								className="trigger-popup"
								onClick={(e) => {
									e.preventDefault();

									const documents: DocumentItem[] = [];
									const getDocumentType = (name: string) => {
										if (name.endsWith('pdf')) return 'pdf';
										if (name.endsWith('jpeg')) return 'jpeg';
										if (name.endsWith('jpg')) return 'jpg';
										if (name.endsWith('png')) return 'png';
									};

									if (
										customerBusinessDocuments &&
										customerBusinessDocuments.length
									) {
										customerBusinessDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (customerLegalDocuments && customerLegalDocuments.length) {
										customerLegalDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (
										customerReferralDocuments &&
										customerReferralDocuments.length
									) {
										customerReferralDocuments
											.filter((doc) => doc.file)
											.forEach((document) => {
												documents.push({
													name: document.documentType,
													type: getDocumentType(document.file.name)!,
													url: document.file.url!,
												});
											});
									}

									if (documents.length) {
										openDocumentModal(documents);
									} else {
										alert('no document found');
									}
								}}
							>
								View Dokumen
							</a>,
						],
					],
					[
						[
							'SLIK Result',
							mainInformation?.slikResult
								? 'Kol ' + mainInformation?.slikResult
								: '-',
						],
						['Divisi Name', null],
					],
					[
						['FBDH Name', mainInformation?.frm.value],
						['FRM Name', mainInformation?.frm.value],
					],
				]}
				style={{ marginBottom: 20 }}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={{
					financialReportAnalysis:
						prospectFinancialReportAnalysis &&
						prospectFinancialReportAnalysis.length
							? prospectFinancialReportAnalysis.map((report) => ({
									id: report.id,
									financialReportId: report.financialReportId,
									averageBalanceInstallmentRatio:
										report.averageBalanceInstallmentRatio,
									averageCollectionPeriod: report.averageCollectionPeriod,
									averagePayablePeriod: report.averagePayablePeriod,
									currentRatio: report.currentRatio,
									debtToEquityRatio: report.debtToEquityRatio,
									eatInstallmentRatio: report.eatInstallmentRatio,
									ebitInstallmentRatio: report.ebitInstallmentRatio,
								}))
							: [],
				}}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName('prospectId')}
					initialValue={mainInformation?.prospectId}
					noStyle
				/>
				<FleetTitle>Prospect Information</FleetTitle>

				<Typography.Title level={3}>
					Informasi Limit Eksisting Pembiayaan
				</Typography.Title>

				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						prospectInformation?.existingFinancingLimitInformation?.map(
							(record, key) => {
								return { ...record, key };
							},
						) ?? []
					}
					columns={[
						{
							title: 'No',
							dataIndex: ['no'],
						},
						{
							title: 'No. MKL',
							dataIndex: ['napNo'],
						},
						{
							title: 'Limit Pembiayaan Existing',
							render: (_, record) =>
								formatCurrency(record.existingFinancingLimit),
						},
						{
							title: 'Jenis Limit Pembiayaan',
							dataIndex: ['financingLimitType'],
						},
						{
							title: 'Tipe Limit',
							dataIndex: ['financingLimitCharacteristic'],
						},
						{
							title: 'Expired Date',
							render: (_, record) => {
								if (record.expiredDate)
									return dayjs(record.expiredDate).format('DD/MM/YYYY');
								return '-';
							},
						},
						{
							title: 'NTF',
							render: (_, record) => formatCurrency(record.ntf) || 0,
						},
						{
							title: 'OS Principal',
							render: (_, record) =>
								formatCurrency(record.outstandingPrincipal),
						},
						{
							title: 'Sisa Limit',
							render: (_, record) =>
								formatCurrency(record.outstandingFinancingLimit),
						},
						{
							title: 'Status',
							dataIndex: ['status'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>

				<Divider />
				<Typography.Title level={3}>
					Informasi Limit Pengajuan Pembiayaan
				</Typography.Title>

				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						prospectInformation?.financingApplicationLimitInformation?.map(
							(record, key) => {
								return { ...record, key };
							},
						) ?? []
					}
					columns={[
						{
							title: 'No',
							dataIndex: ['no'],
						},
						{
							title: 'Limit Pembiayaan Existing',
							render: (_, record) =>
								formatCurrency(record.existingFinancingLimit),
						},
						{
							title: 'Jenis Limit Pembiayaan',
							dataIndex: ['financingLimitType'],
						},
						{
							title: 'OS Principal',
							render: (_, record) =>
								formatCurrency(record.outstandingPrincipal),
						},
						{
							title: 'Tujuan Pembiayaan',
							dataIndex: ['financingPurpose'],
						},
						{
							title: 'Pengajuan Limit',
							render: (_, record) => formatCurrency(record.limitApplication),
						},
						{
							title: 'Total Pengajuan Plafon',
							render: (_, record) =>
								formatCurrency(record.totalLimitApplication),
						},
						{
							title: 'Segmen Market',
							dataIndex: ['segmentMarket'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				<FleetTitle>Verifikasi Data Pra-Survey</FleetTitle>
				<Typography.Title level={3}>Data Legalitas</Typography.Title>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						customerLegalDocuments?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (record.file?.id) {
												openPreviewModal({
													id: record.file.id as unknown as number,
													name: record.file.name,
													url: record.file.url,
												});
											}
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Jenis Dokumen',
							dataIndex: ['documentType'],
						},
						{
							title: 'No Dokumen',
							dataIndex: ['documentNo'],
						},
						{
							title: 'Tanggal Dokumen',
							dataIndex: ['documentDate'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				<Typography.Title level={3}>Data Perizinan Usaha</Typography.Title>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						customerBusinessDocuments?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal(record.file as FleetFile);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Jenis Dokumen',
							dataIndex: ['documentType'],
						},
						{
							title: 'No Dokumen',
							dataIndex: ['documentNo'],
						},
						{
							title: 'Penerbit',
							dataIndex: ['publisher', 'value'],
						},
						{
							title: 'Tanggal Dokumen',
							dataIndex: ['documentDate'],
						},
						{
							title: 'Masa Berlaku',
							dataIndex: ['validityPeriod', 'value'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				{customerReferralDocuments && customerReferralDocuments.length > 0 && (
					<>
						<Typography.Title level={3}>Data Referral JF</Typography.Title>
						<Table
							scroll={{ x: 'max-content' }}
							dataSource={
								customerReferralDocuments?.map((record, key) => {
									return { ...record, key };
								}) ?? []
							}
							columns={[
								{
									title: 'View Detail',
									align: 'center',
									render: (_, record) => {
										return (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													openPreviewModal(record.file as FleetFile);
												}}
											>
												<FileSearchOutlined />
											</a>
										);
									},
								},
								{
									title: 'Jenis Dokumen',
									dataIndex: ['documentType'],
								},
								{
									title: 'No Dokumen',
									dataIndex: ['documentNo'],
								},

								{
									title: 'Tanggal Dokumen',
									dataIndex: ['documentDate'],
								},
							]}
							bordered
							pagination={false}
							style={{ marginBottom: 20 }}
						/>
						<Divider />
					</>
				)}

				{/* {mainInformation} */}

				<Typography.Title level={3}>Data Kontrak Kerja</Typography.Title>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						employmentContracts?.map((record, key) => {
							return {
								...record,
								key,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'View Kontrak Kerja',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openEmploymentContractModal(record);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Bouwheer',
							dataIndex: ['bouwheer', 'name'],
						},
						{
							title: 'Bouwheer ID',
							dataIndex: ['bouwheer', 'id'],
						},
						{
							title: 'Kelas Industri',
							dataIndex: ['bouwheer', 'industrySector', 'industryClass'],
						},
						{
							title: 'Nama Pekerjaan',
							dataIndex: ['jobTitle'],
						},
						{
							title: 'No. SPK',
							dataIndex: ['spkNumber'],
						},
						{
							title: 'Lokasi Pekerjaan',
							dataIndex: ['jobLocation'],
						},
						{
							title: 'Detail Kontrak',
							dataIndex: ['contractDetail'],
						},
						{
							title: 'Nilai Kontrak',
							render: (_, record) => formatCurrency(record.contractValue),
						},
						{
							title: 'Tenor',
							dataIndex: ['tenor'],
							render: (_, record) => record.tenor ?? '-',
						},
						{
							title: 'Sistem Pembayaran',
							dataIndex: ['paymentMethod', 'value'],
							render: (_, record) => record.paymentMethod?.value ?? '-',
						},
						{
							title: 'TOP Pembayaran',
							dataIndex: ['termOfPayment'],
							render: (_, record) => record.termOfPayment ?? '-',
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				<Typography.Title level={3}>Data Rekening Bank</Typography.Title>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						prospectBankStatements?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openBankStatementModal(record);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Jenis Rekening	',
							dataIndex: ['accountType', 'value'],
						},
						{
							title: 'Nama Bank',
							dataIndex: ['bankName', 'value'],
						},
						{
							title: 'Account Number',
							dataIndex: ['accountNo'],
						},
						{
							title: 'Periode',
							render: (_, record) =>
								dayjs(record.bankStatementPeriod).format('MM/YYYY'),
						},
						{
							title: 'Total Debet',
							dataIndex: ['totalDebit'],
						},
						{
							title: 'Total Kredit',
							dataIndex: ['totalCredit'],
						},
						{
							title: 'Average Saldo',
							dataIndex: ['averageBalance'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				<Typography.Title
					level={3}
					id="laporan-keuangan"
				>
					Financial Information
				</Typography.Title>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						prospectFinancialReports?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							align: 'center',
							render: (_, record) => {
								return (
									<Checkbox
										checked={checkedFinancialId.includes(record.id)}
										disabled={
											checkedFinancialId.length === 2 &&
											!checkedFinancialId.includes(record.id)
										}
										onChange={(e) => {
											if (e.target.checked) {
												setCheckedFinancialId((ids) => {
													return [...ids, record.id];
												});
											} else {
												setCheckedFinancialId((ids) => {
													return ids.filter((id) => id !== record.id);
												});
											}
										}}
									/>
								);
							},
						},
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openFinancialReportModal(record);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Laporan Keuangan',
							dataIndex: ['auditStatus', 'value'],
						},
						{
							title: 'Nama Auditor',
							dataIndex: ['auditorName'],
						},
						{
							title: 'Periode',
							dataIndex: ['periodType', 'value'],
						},
						{
							title: 'Detail Periode',
							dataIndex: ['periodTypeDetail', 'value'],
						},
						{
							title: 'Tahun',
							dataIndex: ['year'],
						},
						{
							title: 'View Dokumen',
							render: (_, record) => {
								return (
									<a
										href="#"
										className="trigger-popup"
										onClick={(e) => {
											e.preventDefault();
											openPreviewModal({
												name: record.financialReportFile.name,
												url: record.financialReportFile.url,
												id: record.financialReportFile.id as unknown as number,
											});
										}}
									>
										View Dokumen
									</a>
								);
							},
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Space
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						marginBottom: 20,
					}}
					size="large"
				>
					<Button
						style={{
							width: 200,
							display: 'block',
						}}
						onClick={() => {
							form.setFieldValue(fieldName('financialReportAnalysis'), []);
							setCheckedFinancialId([]);
						}}
					>
						BATAL
					</Button>
					<Button
						type="primary"
						style={{
							width: 200,
							display: 'block',
							marginLeft: 'auto',
						}}
						disabled={
							checkedFinancialId.length !== 2 ||
							value?.financialReportAnalysis?.length === 2
						}
						onClick={() => {
							const financialReportAnalysis =
								[] as SubmitCCASurveyAnalysisPayload['financialReportAnalysis'];

							if (
								prospectFinancialReportAnalysis &&
								prospectFinancialReportAnalysis.length
							) {
								prospectFinancialReportAnalysis.forEach((report) => {
									if (!checkedFinancialId.includes(report.financialReportId))
										return;
									financialReportAnalysis.push({
										id: report.id,
										financialReportId: report.financialReportId,
										averageBalanceInstallmentRatio:
											report.averageBalanceInstallmentRatio,
										averageCollectionPeriod: report.averageCollectionPeriod,
										averagePayablePeriod: report.averagePayablePeriod,
										currentRatio: report.currentRatio,
										debtToEquityRatio: report.debtToEquityRatio,
										eatInstallmentRatio: report.eatInstallmentRatio,
										ebitInstallmentRatio: report.ebitInstallmentRatio,
									});
								});
							} else {
								prospectFinancialReports?.forEach((report) => {
									if (!checkedFinancialId.includes(report.id)) return;
									financialReportAnalysis.push({
										id: report.id,
										financialReportId: report.id,
										averageBalanceInstallmentRatio: 0,
										averageCollectionPeriod: 0,
										averagePayablePeriod: 0,
										currentRatio: 0,
										debtToEquityRatio: 0,
										eatInstallmentRatio: 0,
										ebitInstallmentRatio: 0,
									});
								});
							}

							form.setFieldValue(
								fieldName('financialReportAnalysis'),
								financialReportAnalysis,
							);
						}}
					>
						LAKUKAN KOMPARASI
					</Button>
				</Space>

				<Form.Item
					name={fieldName('financialReportAnalysis')}
					noStyle
				/>

				<Form.Item shouldUpdate>
					{({ getFieldsValue }) => {
						const { financialReportAnalysis } =
							getFieldsValue() as SubmitCCASurveyAnalysisPayload;

						if (
							!financialReportAnalysis ||
							financialReportAnalysis.filter(
								(report) => report.financialReportId,
							).length !== 2
						) {
							return null;
						}

						const financialReports = financialReportAnalysis.map((record) => {
							return prospectFinancialReports?.find(
								(report) => report.id === record.financialReportId,
							);
						});

						return (
							<table className="table-compare">
								<thead>
									<tr>
										<td />
										{financialReports.map((report, index) => (
											<td key={index}>
												{report?.year}
												<span
													style={{ marginLeft: 8, cursor: 'pointer' }}
													onClick={(e) => {
														e.preventDefault();
														openFinancialReportModal(
															report as ProspectFinancialReport,
														);
													}}
												>
													<FileSearchOutlined />
												</span>
											</td>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										<td>Detail Periode</td>
										{financialReports.map((report, index) => (
											<td key={index}>
												<Input
													disabled
													value={report?.periodTypeDetail.value}
												/>
											</td>
										))}
									</tr>
									<tr>
										<td>EAT/Angsuran 1 TH (1X S/D 0.5X)</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'eatInstallmentRatio',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
									<tr>
										<td>EBIT/Angsuran 1 TH (3X S/D 1X)</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'ebitInstallmentRatio',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
									<tr>
										<td>Rata-Rata Saldo Mengendap/ Angsuran 1 Bulan</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'averageBalanceInstallmentRatio',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
									<tr>
										<td>Current Ratio*</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'currentRatio',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
									<tr>
										<td>Debt to Equity Ratio*</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'debtToEquityRatio',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
									<tr>
										<td>Average Collection Period (days)*</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'averageCollectionPeriod',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
									<tr>
										<td>Average Payable Period (days)*</td>
										{financialReportAnalysis.map((_, index) => (
											<td key={index}>
												<Form.Item
													name={fieldName([
														'financialReportAnalysis',
														index,
														'averagePayablePeriod',
													])}
													noStyle
												>
													<InputNumber />
												</Form.Item>
											</td>
										))}
									</tr>
								</tbody>
							</table>
						);
					}}
				</Form.Item>

				<FleetTitle>Survey Information</FleetTitle>
				<Row gutter={[40, 0]}>
					<Col span={12}>
						<Form.Item
							label="Lokasi Survey Kantor"
							labelAlign="left"
							labelCol={{ span: 10 }}
						>
							<div style={{ fontSize: 16, marginTop: 10 }}>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openMapModal({
											title: 'Lokasi Survey Kantor',
											address: officeAddress,
											latitude: officeLat,
											longitude: officeLong,
											onChange: ({ address, center }) => {
												setOfficeAddress(address);
												setOfficeLat(center.lat);
												setOfficeLong(center.lng);
											},
											draggable: false,
											viewOnly: true,
										});
									}}
								>
									{surveyInformation?.officeAddress}
								</a>
								,
							</div>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('surveyOfficeDate')}
							label="Tanggal Survey Kantor"
							labelAlign="left"
							labelCol={{ span: 10 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
							initialValue={dayjs(surveyInformation?.surveyOfficeDate)}
						>
							<DatePicker
								disabledDate={(now) => now < dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
				</Row>
				<Divider />
				<Row gutter={[40, 0]}>
					<Col span={12}>
						<Form.Item
							label="Lokasi Survey Site"
							labelAlign="left"
							labelCol={{ span: 10 }}
						>
							<div style={{ fontSize: 16, marginTop: 10 }}>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										openMapModal({
											title: 'Lokasi Survey Site',
											address: siteAddress,
											latitude: siteLat,
											longitude: siteLong,
											onChange: ({ address, center }) => {
												setSiteAddress(address);
												setSiteLat(center.lat);
												setSiteLong(center.lng);
											},
											draggable: false,
											viewOnly: true,
										});
									}}
								>
									{surveyInformation?.siteAddress}
								</a>
								,
							</div>
						</Form.Item>
					</Col>
					<Col span={12}>
						<Form.Item
							name={fieldName('surveySiteDate')}
							label="Tanggal Survey Site"
							labelAlign="left"
							labelCol={{ span: 10 }}
							getValueProps={(value) => ({
								value: value && dayjs(value),
							})}
							normalize={(value) =>
								value && dayjs(value).utc(true).toISOString()
							}
							rules={[rule.required()]}
							initialValue={dayjs(surveyInformation?.surveySiteDate)}
						>
							<DatePicker
								disabledDate={(now) => now < dayjs().subtract(1, 'day')}
							/>
						</Form.Item>
					</Col>
				</Row>

				<Divider />
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						surveyParticipants?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							render: (_, record) => record.key + 1,
						},
						{
							title: 'Nama Peserta Survey',
							dataIndex: ['name'],
						},
						{
							title: 'Jabatan Peserta Survey',
							dataIndex: ['position'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>

				<FleetTitle>Approval History</FleetTitle>
				<Table
					dataSource={
						approvalHistories?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'No',
							width: 1,
							align: 'center',
							dataIndex: 'no',
						},
						{
							title: 'Name',
							dataIndex: 'approvalBy',
						},
						{
							title: 'Date',
							dataIndex: 'approvalDate',
						},
						{
							title: 'Process',
							dataIndex: 'approvalProcess',
						},
						{
							title: 'Status',
							align: 'center',
							render: (_, record) => {
								return (
									<span
										className={`review-status ${record.approvalStatus.toLowerCase()}`}
									>
										{record.approvalStatus}
									</span>
								);
							},
						},
						{
							title: 'Approval Notes',
							dataIndex: 'approvalNote',
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Form.Item
					name={fieldName('notes')}
					label="Verification Notes"
					labelAlign="left"
					labelCol={{
						span: 4,
						style: {
							alignSelf: 'center',
						},
					}}
					rules={[rule.required()]}
				>
					<TextArea rows={3} />
				</Form.Item>
				<Space
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						marginTop: 40,
					}}
					size="large"
				>
					<Button
						style={{
							width: 200,
							display: 'block',
						}}
						loading={submitCCAReturnAnalysisMutation.isPending}
						onClick={async () => {
							const isConfirmed = await modal.confirm({
								title: 'Return Survey?',
							});
							if (!isConfirmed) return;
							await submitCCAReturnAnalysisMutation.mutateAsync({
								notes: value!.notes!,
								prospectId: mainInformation!.prospectId,
							});
							navigate('CCASurveyVerificationListPage');
						}}
					>
						RETURN
					</Button>
					<Button
						type="primary"
						loading={submitCCASurveyAnalysisMutation.isPending}
						htmlType="submit"
						style={{
							width: 200,
							display: 'block',
							marginLeft: 'auto',
						}}
					>
						SAVE
					</Button>
				</Space>
			</Form>
		</Fragment>
	);
};

export default CCASurveyVerificationPage;
